/**
 * OR-6455: build a `UseEpiChatPageResult`-shaped object from a one-shot
 * snapshot, WITHOUT standing up a live socket subscription.
 *
 * Folds the snapshot through the real `epiChatReducer` (the exact same
 * reducer `useEpiChatPage` uses) via a single `hydrated()` action, called
 * as a pure function against `initialEpiChatState` — never dispatched
 * against the app's own Redux store. This reuses the live path's
 * message/artifact reconciliation logic instead of re-deriving it, while
 * guaranteeing there is no socket, no room, and no reachable write path:
 * every action callback below is a no-op.
 */

import {
  epiChatActions,
  epiChatReducer,
  initialEpiChatState,
  type EpiArtifactTab,
  type UseEpiChatPageResult,
} from '@verantos-dev/feature-epi-chat';

import type { InternalChatSnapshot } from '../api/types';

const noop = () => {
  /* read-only: every action callback is intentionally inert */
};

/**
 * `snapshot` is `null` for a chat with nothing to replay yet (matches
 * `hydrated()`'s own empty-state handling — the panel just renders its
 * normal empty state).
 */
export const buildReadOnlyChatPage = (
  chatId: string,
  snapshot: InternalChatSnapshot['snapshot']
): UseEpiChatPageResult => {
  const state = snapshot
    ? epiChatReducer(
        initialEpiChatState,
        epiChatActions.hydrated({
          sessionId: chatId,
          messages: snapshot.messages,
          artifacts: snapshot.artifacts,
          activeArtifactId: null,
          activeArtifactTab: null,
        })
      )
    : initialEpiChatState;

  return {
    status: 'connected',
    messages: state.messages,
    queue: [],
    runState: 'idle',
    error: null,
    initializing: false,
    subscribing: false,
    subscribeAcked: true,
    artifacts: state.artifacts,
    activeArtifact:
      state.artifacts.find((artifact) => artifact.id === state.activeArtifactId) ?? null,
    activeArtifactTab: state.activeArtifactTab as EpiArtifactTab | null,
    activeSessionId: chatId,
    sendMessage: noop,
    cancelRun: noop,
    beginUpload: noop,
    endUpload: noop,
    beginResume: noop,
    endResume: noop,
    dequeueMessage: noop,
    selectArtifact: noop,
    selectArtifactTab: noop,
    applyArtifactScope: noop,
    remoteFocusScope: null,
    interruptedRun: null,
    retryInterruptedRun: noop,
  };
};
